import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CONTACT } from "@/constants/testIds/ashwin";
import { profile } from "@/data/resume";
import { ArrowUpRight, Mail, Phone, Linkedin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received. I'll get back to you soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={CONTACT.root}
      className="py-24 md:py-40 border-t border-[color:var(--border)]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-y-16 md:gap-x-16">
        <div className="col-span-12 md:col-span-5">
          <div className="text-xs font-mono-editorial uppercase text-[color:var(--muted)] tracking-[0.15em] mb-6">
            06 — Contact
          </div>
          <h2 className="font-display text-6xl md:text-7xl lg:text-8xl font-light tracking-tight leading-[0.9]">
            Let&rsquo;s <br />
            <span className="font-serif-accent italic text-[color:var(--accent)]">talk shop.</span>
          </h2>
          <p className="mt-8 text-lg leading-relaxed max-w-md text-[color:var(--charcoal)]/80">
            Have a brand to launch, a category to crack, or just want to swap notes on the
            marketing-strategy overlap? Drop a line.
          </p>

          <div className="mt-12 space-y-4 text-base">
            <a
              href={`mailto:${profile.email}`}
              className="editorial-link flex items-center gap-3 hover:text-[color:var(--accent)] transition-colors"
            >
              <Mail className="w-4 h-4" /> {profile.email}
            </a>
            <a
              href={`tel:${profile.phone}`}
              className="editorial-link flex items-center gap-3 hover:text-[color:var(--accent)] transition-colors"
            >
              <Phone className="w-4 h-4" /> {profile.phone}
            </a>
            <a
              href={profile.linkedin}
              target="_blank"
              rel="noreferrer"
              className="editorial-link flex items-center gap-3 hover:text-[color:var(--accent)] transition-colors"
            >
              <Linkedin className="w-4 h-4" /> linkedin.com/in/ashwinkalra
            </a>
          </div>
        </div>

        <form
          onSubmit={submit}
          className="col-span-12 md:col-span-6 md:col-start-7 space-y-2"
        >
          <div className="grid md:grid-cols-2 gap-8 mb-6">
            <div>
              <label className="text-xs font-mono-editorial uppercase text-[color:var(--muted)] tracking-[0.15em]">
                Name
              </label>
              <input
                data-testid={CONTACT.name}
                className="editorial-input"
                value={form.name}
                onChange={set("name")}
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="text-xs font-mono-editorial uppercase text-[color:var(--muted)] tracking-[0.15em]">
                Email
              </label>
              <input
                data-testid={CONTACT.email}
                type="email"
                className="editorial-input"
                value={form.email}
                onChange={set("email")}
                placeholder="you@company.com"
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="text-xs font-mono-editorial uppercase text-[color:var(--muted)] tracking-[0.15em]">
              Subject
            </label>
            <input
              data-testid={CONTACT.subject}
              className="editorial-input"
              value={form.subject}
              onChange={set("subject")}
              placeholder="What is this about?"
            />
          </div>

          <div className="mb-8">
            <label className="text-xs font-mono-editorial uppercase text-[color:var(--muted)] tracking-[0.15em]">
              Message
            </label>
            <textarea
              data-testid={CONTACT.message}
              rows={5}
              className="editorial-input resize-none"
              value={form.message}
              onChange={set("message")}
              placeholder="Tell me a bit about what you're building…"
            />
          </div>

          <button
            data-testid={CONTACT.submit}
            type="submit"
            disabled={loading}
            className="group inline-flex items-center gap-3 px-8 py-4 bg-[color:var(--charcoal)] text-white text-sm md:text-base font-medium hover:bg-[color:var(--accent)] transition-colors duration-300 disabled:opacity-60"
          >
            {loading ? "Sending…" : "Send message"}
            <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
          </button>
        </form>
      </div>
    </section>
  );
}
